import React from 'react';
import { IRoomFilters } from '../type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRemove } from '@fortawesome/free-solid-svg-icons';
import { sumBy } from 'lodash';
import { useLocation } from 'react-router-dom';
import { getCurrentSearchQuery } from '@/urls';
import { AvailableRoomModel } from '@/__generated';

type Props = {
  roomFilters: IRoomFilters | undefined | null;
  onBoardTypeFilterChange: (k: string) => void;
  onRoomTypeFilterChange: (k: string) => void;
  selectedRoomTypes: string[];
  selectedBoardTypes: string[];
  clearAllFilter: () => void;
  roomProviderFilter: string | undefined;
  onRoomProviderFilterChange: (provider: string | undefined) => void;
  ratePlanFilter: string | undefined;
  onRatePlanFilterChange: (ratePlan: string | undefined) => void;
  availableRooms: AvailableRoomModel[];
};

export default function RoomFilters({
  roomFilters,
  onBoardTypeFilterChange,
  onRoomTypeFilterChange,
  selectedBoardTypes,
  selectedRoomTypes,
  clearAllFilter,
  roomProviderFilter,
  onRoomProviderFilterChange,
  ratePlanFilter,
  onRatePlanFilterChange,
  availableRooms,
}: Props) {
  if (!roomFilters) {
    return null;
  }
  const { search } = useLocation();
  const ntaRoomCount = React.useMemo(() => {
    return sumBy(
      roomFilters.roomProviders.filter(x => x.key === 'NTA'),
      x => x.count,
    );
  }, [roomFilters]);
  const allRoomCount = React.useMemo(() => {
    return sumBy(roomFilters.roomProviders, x => x.count);
  }, [roomFilters]);

  const displayRatePlanFilter = React.useMemo(() => {
    return !!getCurrentSearchQuery('campaignRatePlan');
  }, [search]);

  const { campaignPlan, totalPlan } = React.useMemo(() => {
    let campaignPlan = 0;
    let totalPlan = 0;
    let campaignRatePlan = getCurrentSearchQuery('campaignRatePlan');
    if (campaignRatePlan) {
      let planCodes = campaignRatePlan.split(',');
      availableRooms.forEach(availableRoom => {
        availableRoom.availableRoomTypes?.forEach(roomType => {
          roomType.roomPlans?.forEach(plan => {
            if (planCodes.includes(plan.code || '')) {
              campaignPlan += 1;
            }
            totalPlan += 1;
          });
        });
      });
    }

    return {
      campaignPlan,
      totalPlan,
    };
  }, [displayRatePlanFilter]);

  const { refundable, nonRefundable } = React.useMemo(() => {
    let refundable = 0;
    let nonRefundable = 0;
    availableRooms.forEach(availableRoom => {
      availableRoom.availableRoomTypes?.forEach(roomType => {
        roomType.roomPlans?.forEach(plan => {
          if (plan.nonRefundable) {
            nonRefundable += 1;
          } else {
            refundable += 1;
          }
        });
      });
    });

    return {
      refundable,
      nonRefundable,
    };
  }, [availableRooms]);

  const hasFilter = React.useMemo(() => {
    return selectedRoomTypes.length || selectedBoardTypes.length || roomProviderFilter === 'nta' || !!ratePlanFilter;
  }, [selectedRoomTypes, selectedBoardTypes, roomProviderFilter, ratePlanFilter]);
  return (
    <div className=" mb-10 border-light rounded px-10 py-10">
      <div className="fw-500 text-16">
        Quick search
        {hasFilter ? (
          <button className="text-red-1 ml-10  text-14 fw-500" onClick={() => clearAllFilter()}>
            <FontAwesomeIcon icon={faRemove} className="mr-5" />
            Clear filter
          </button>
        ) : null}
      </div>
      <div className="x-gap-10 row mt-10 y-gap-10">
        {roomFilters.boardTypes.map(board => {
          return (
            <div className="col-auto" key={board.key}>
              <button
                onClick={() => onBoardTypeFilterChange(board.key)}
                className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${
                  selectedBoardTypes?.includes(board.key) ? 'bg-blue-1 text-white' : ''
                } `}
              >
                {board.label}
                <span className="fw-500 ml-5">{`(${board.count})`}</span>
              </button>
            </div>
          );
        })}
        {roomFilters.roomTypes.map(room => {
          return (
            <div className="col-auto" key={room.key}>
              <button
                onClick={() => onRoomTypeFilterChange(room.key)}
                className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${
                  selectedRoomTypes?.includes(room.key) ? 'bg-blue-1 text-white' : ''
                } `}
              >
                {room.label}
                <span className="fw-500 ml-5">{`(${room.count})`}</span>
              </button>
            </div>
          );
        })}
      </div>

      <div className="x-gap-10 row mt-10 y-gap-10">
        <div className="col-auto">
          <button
            onClick={() => onRoomProviderFilterChange('nta')}
            className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${roomProviderFilter === 'nta' ? 'bg-blue-1 text-white' : ''} `}
          >
            Supplier NTA
            <span className="fw-500 ml-5">{`(${ntaRoomCount})`}</span>
          </button>
        </div>
        <div className="col-auto">
          <button
            onClick={() => onRoomProviderFilterChange('_')}
            className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${roomProviderFilter !== 'nta' ? 'bg-blue-1 text-white' : ''} `}
          >
            All Suppliers
            <span className="fw-500 ml-5">{`(${allRoomCount})`}</span>
          </button>
        </div>
        <div className="col-auto">
          <button className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 bg-blue-1 text-white `}>
            Refundable
            <span className="fw-500 ml-5">{`(${refundable})`}</span>
          </button>
        </div>
        <div className="col-auto">
          <button className={`button border-light rounded-5 px-20 py-10 text-14  bg-light-1 text-white `}>
            Non-refundable
            <span className="fw-500 ml-5">{`(${nonRefundable})`}</span>
          </button>
        </div>
      </div>
      {displayRatePlanFilter ? (
        <div className="x-gap-10 row mt-10 y-gap-10">
          <div className="col-auto">
            <button
              onClick={() => onRatePlanFilterChange(getCurrentSearchQuery('campaignRatePlan') || undefined)}
              className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${ratePlanFilter ? 'bg-blue-1 text-white' : ''} `}
            >
              Campaign Plan
              <span className="fw-500 ml-5">{`(${campaignPlan})`}</span>
            </button>
          </div>
          <div className="col-auto">
            <button
              onClick={() => onRatePlanFilterChange(undefined)}
              className={`button border-light rounded-5 px-20 py-10 text-14 text-dark-1 ${!ratePlanFilter ? 'bg-blue-1 text-white' : ''} `}
            >
              All Plan
              <span className="fw-500 ml-5">{`(${totalPlan})`}</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
